import Data from '@/data'
import Trans from '@/data/trans'
import { GemDefinition, GemEffects, Range } from '@/data/types'
import { TooltipLine } from '@/types/common'
import { Gem } from '@/types/item'
import { format } from '@/utils/format'
import { RangeObject, modMakeRange } from './mods'

export function gemMaxLevel(id: string) {
  const data = Data.gems[id]
  if (data.base_item?.max_level) return data.base_item.max_level
  let level = 20
  while (level > 1 && !data.per_level[level]) {
    level -= 1
  }
  return level
}

export function gemCreate(id: string, source?: Gem) {
  const gem: Gem = {
    id,
    quality: source?.quality || 20,
    corrupted: source?.corrupted || false,
    level: source?.level || gemMaxLevel(id)
  }
  if (Data.gems[id].tags?.includes('vaal')) gem.corrupted = true
  return gem
}

export type GemComputedProperties = {
  level: number
  quality: number
  stats: Record<string, number>
  quality_stats?: Record<string, number>
} & Omit<GemEffects, 'stats' | 'quality_stats'>

export function gemGetProperties(gem: Gem, separateQuality?: boolean) {
  function extend(dst: any, src: any) {
    for (const [key, value] of Object.entries(src)) {
      if (value && typeof value === 'object') {
        const clone = dst[key] && typeof dst[key] === 'object' ? dst[key] : Array.isArray(value) ? [] : {}
        dst[key] = extend(clone, value)
      } else if (value != null) {
        dst[key] = value
      }
    }
    return dst
  }

  const data = Data.gems[gem.id]
  const effects: Partial<GemEffects> = {}
  extend(effects, data.static)
  if (data.per_level[gem.level]) extend(effects, data.per_level[gem.level])

  const { stats, quality_stats, ...other } = effects as GemEffects

  const props: GemComputedProperties = {
    level: gem.level,
    quality: gem.quality,
    stats: {},
    ...other
  }

  if (stats) {
    for (const stat of stats) {
      if (stat) props.stats[stat.id] = (props.stats[stat.id] || 0) + stat.value
    }
  }

  if (quality_stats && gem.quality) {
    const qstats = separateQuality ? {} : props.stats
    for (const stat of quality_stats) {
      qstats[stat.id] = (qstats[stat.id] || 0) + Math.floor((stat.value * gem.quality) / 1000)
    }
    if (separateQuality) props.quality_stats = qstats
  }

  return props
}

type GemDescription = {
  name: string
  properties: TooltipLine[]
  requirements: TooltipLine[]
  stats: TooltipLine[]
  description?: TooltipLine[]
}

export function gemName(gem: GemDefinition) {
  return gem.active_skill?.display_name ?? gem.base_item?.display_name ?? 'Unknown'
}

export function gemFormatProperties(gem: Gem, props: RangeObject<GemComputedProperties>) {
  const data = Data.gems[gem.id]

  const result: GemDescription = {
    name: gemName(data),
    properties: [],
    requirements: [],
    stats: []
  }

  const is_ge = (value: number | Range, base: number = 0) =>
    typeof value === 'object' ? value.min >= base && value.max >= base : value >= base
  const fmt_number = (value: number | Range, prec: number = 0, mult: number = 1, add: number = 0): string =>
    typeof value === 'object'
      ? `(${fmt_number(value.min, prec, mult, add)}-${fmt_number(value.max, prec, mult, add)})`
      : (value * mult + add).toFixed(prec)

  const types = data.active_skill?.types || []

  let lines = result.properties
  const fmt_span = (text: string | number, style: string) => `<${style}>{${text}}`
  const fmt_value = (text: string | number, type: string = 'Default') => fmt_span(text, `colour${type}`)
  const add_line = (text: string, type: string = 'displayProperty') => lines.push({ text, type })

  const tags = data.tags?.map((tag) => Data.gem_tags[tag]).filter(Boolean)
  if (tags?.length) add_line(tags.join(', '))

  add_line(`Level: ${fmt_value(fmt_number(props.level) + (is_ge(props.level, gemMaxLevel(gem.id)) ? ' (Max)' : ''))}`)

  if (props.cost_multiplier && props.cost_multiplier !== 100) {
    add_line(`Cost & Reservation Multiplier: ${fmt_value(fmt_number(props.cost_multiplier) + '%')}`)
  }

  if (props.costs) {
    const value = fmt_value(
      Object.entries(props.costs)
        .map(([id, value]) => {
          const type = Data.cost_types[id]
          if (!type) return ''
          return format(type.format_text, fmt_number(value, 0, 1 / (type.divide || 1)))
        })
        .join(', ')
    )
    add_line(`Cost: ${value}`)
  }

  if (props.reservations) {
    const value = fmt_value(
      Object.entries(props.reservations)
        .map(([id, value]) => {
          const type = Data.reservationTypes[id]
          if (!type) return ''
          return format(type, fmt_number(value))
        })
        .join(', ')
    )
    add_line(`Reservation: ${value}`)
  }

  if (props.vaal) {
    add_line(`Souls Per Use: ${fmt_value(fmt_number(props.vaal.souls))}`)
    add_line(
      `Can Store ${fmt_value(fmt_number(props.vaal.stored_uses))} ${is_ge(props.vaal.stored_uses, 2) ? 'Use' : 'Uses'}`
    )
  }

  if (props.cooldown) {
    add_line(`Cooldown Time: ${fmt_value(`${fmt_number(props.cooldown, 2, 0.001)} sec`)}`)
    if (props.stored_uses) add_line(`Can Store ${fmt_value(fmt_number(props.stored_uses))} Use(s)`)
  }

  if (data.cast_time !== 1000) {
    add_line(`Cast Time: ${fmt_value(data.cast_time ? `${fmt_number(data.cast_time, 2, 0.001)} sec` : 'Instant')}`)
  }

  if (props.crit_chance) {
    add_line(`Critical Strike Chance: ${fmt_value(fmt_number(props.crit_chance, 2, 0.01) + '%')}`)
  }

  if (props.attack_speed_multiplier) {
    add_line(`Attack Speed: ${fmt_value(`${fmt_number(props.attack_speed_multiplier, 0, 1, 100)}% of base`)}`)
  }

  if (props.damage_multiplier) {
    add_line(`Attack Damage: ${fmt_value(`${fmt_number(props.damage_multiplier, 0, 0.01, 100)}% of base`)}`)
  }

  if (props.damage_effectiveness) {
    add_line(`Effectiveness of Added Damage: ${fmt_value(`${fmt_number(props.damage_effectiveness, 0, 1, 100)}%`)}`)
  }

  if (gem.quality) {
    add_line(`Quality: ${fmt_value(`+${gem.quality}%`, 'Augmented')}`)
  }

  lines = result.requirements

  const reqs = []
  if (is_ge(props.required_level, 2)) {
    reqs.push(`Level ${fmt_value(fmt_number(props.required_level))}`)
  }
  if (props.stat_requirements) {
    const printouts = { str: 'Str', dex: 'Dex', int: 'Int' }
    for (const [stat, value] of Object.entries(props.stat_requirements)) {
      if (is_ge(value, 15)) reqs.push(`${printouts[stat as keyof typeof printouts]} ${fmt_value(fmt_number(value))}`)
    }
  }
  if (reqs.length) {
    add_line(`Requires ${reqs.join(', ')}`, 'requirements')
  }

  let description: TooltipLine[] | undefined
  const gemDescr = data.active_skill?.description ?? data.support_gem?.support_text
  if (gemDescr) {
    lines = result.description = []
    add_line(gemDescr, 'secDescrText')
  }

  lines = result.stats
  const tr = data.stat_translation_file?.match(/^stat_translations\/(.*)$/)

  if (props.stats) {
    const stats = Trans[tr?.[1] || 'global'].formatStats(props.stats)
    for (const stat of stats) {
      add_line(stat, 'explicitMod')
    }
  }
  if (props.quality_stats && Object.values(props.quality_stats).some(Boolean)) {
    add_line('', 'colourDefault')
    add_line('Additional Effects From Quality:', 'colourDefault')
    const stats = Trans[tr?.[1] || 'global'].formatStats(props.quality_stats)
    for (const stat of stats) {
      add_line(stat, 'explicitMod')
    }
  }

  if (gem.corrupted) {
    add_line('Corrupted', 'unmet')
  }

  return result
}

export function gemTemplateProps(id: string) {
  const gem = gemCreate(id)
  if (!gem) return
  const propsMax = gemGetProperties(gem, true)
  gem.level = 1
  gem.quality = 0
  const propsMin = gemGetProperties(gem, true)
  const props = modMakeRange(propsMin, propsMax)
  return gemFormatProperties(gem, props)
}

const validTransfigures = [1, 2]

export function gemItemIcon(skill: GemDefinition) {
  const base = skill.base_item && Data.items[skill.base_item.id]
  if (!base) return
  return skill.active_skill?.transfigured && validTransfigures.includes(skill.active_skill.transfigured)
    ? base.visual_identity.dds_file + `_alt${skill.active_skill?.transfigured}`
    : base.visual_identity.dds_file
}

const blacklist = new Set([
  'Icefire',
  'Riptide',
  'WandTeleport',
  'DamageInfusion',
  'Ignite',
  'FireWeapon',
  'Backstab',
  'NewShockNova',
  'NewPunishment',
  'VortexOld',
  'InfernalSwarm',
  'ConvocationOld',
  'DoryanisTouch',
  'StaticTether',
  'NewNewBladeVortex',
  'RighteousLightning',
  'ShadowBlades',
  'AncestralProtector',
  'AncestorTotemSlash',
  'AncestralWarchief',
  'ElementalProjectiles',
  'SummonSkeletonsChannelled',
  'InfernalSweep',
  'SpectralSpinningWeapon',
  'CaptureMonster',
  'VaalAncestralWarchief',
  'BoneArmour',
  'OilArrow',
  'DeathAura',
  'Envy',
  'BlindingAura',
  'BloodOffering',
  'QuickstepHardMode',
  'PlaytestAttack',
  'PlaytestSpell',
  'PlaytestSlam',
  'FlameDashAltX',
  'VaalHeavyStrike',
  'VaalSweep',
  'VaalFireTrap',
  'VaalSplitArrow',
  'VaalFleshOffering',
  'GluttonyOfElements',
  'SupportDivineBlessing',
  'SupportEarthbreaker',
  'SupportLesserReducedManaCost',
  'SupportSplitProjectiles',
  'SupportVaalSoulHarvesting',
  'SupportCastLinkedCursesOnCurse'
])

export function validateGem(gem: GemDefinition, id: string) {
  if (!gem.base_item || !Data.items[gem.base_item.id]) return false
  if (blacklist.has(id)) return false
  const name = gemName(gem)
  if (!name || name === '...') return false
  if (name.includes('[DNT]') || name.includes('[UNUSED]')) return false
  if (name.includes('NOT')) return false
  // Level 10 cap is used for Royale gems
  if (gem.base_item?.max_level === 10) return false
  return true
}
